// 文件清单看板的配置弹窗（管理员）：在大类里加文件、勾它提供哪些字段；新增字段。
// 改的是任务类型的 manifestSchema，对这个操作的所有任务生效。
import { useState } from "react";
import type { ManifestField, ManifestFileReq, ManifestSchema } from "@csp/shared";
import { errorText } from "../lib/api";

type FieldType = ManifestField["type"];
const TYPES: [FieldType, string][] = [["string", "文字"], ["text", "长文字"], ["date", "日期"], ["number", "数字"], ["boolean", "是 / 否"], ["enum", "下拉选项"]];

/** 新字段 key：custom1、custom2 …（不能从中文名生成） */
function newFieldKey(schema: ManifestSchema): string {
  let n = 1;
  while (schema.fields.some((f) => f.key === `custom${n}`)) n++;
  return `custom${n}`;
}
function newFileKey(schema: ManifestSchema): string {
  let n = 1;
  while (schema.files.some((f) => f.key === `doc-${n}`)) n++;
  return `doc-${n}`;
}
/**
 * 改完清单要不要重读材料（AI 要几十秒）：加了要识别的字段、加了文件类别、改了字段由哪些文件提供才要；
 * 只加直接输入的字段、删字段、改名 / 必填，刷新一下结果就够
 */
export function schemaNeedsReread(prev: ManifestSchema | undefined, next: ManifestSchema): boolean {
  if (!prev) return true;
  const old = new Map(prev.fields.map((f) => [f.key, f]));
  const oldFiles = new Set(prev.files.map((f) => f.key));
  if (next.files.some((f) => !oldFiles.has(f.key))) return true;
  return next.fields.some((f) => {
    const o = old.get(f.key);
    if (!o) return !f.inputOnly;
    return !f.inputOnly && [...(f.sourceFiles ?? [])].sort().join() !== [...(o.sourceFiles ?? [])].sort().join();
  });
}

const splitWords = (s: string) => s.split(/[,，、;；\n]/).map((w) => w.trim()).filter(Boolean);

/** 在某个大类里加一个文件方块，并勾选它提供的字段（可顺手新建字段） */
export function AddFileDialog({ schema, group, onSave, onClose }: { schema: ManifestSchema; group: string; onSave: (s: ManifestSchema) => Promise<void>; onClose: () => void }) {
  const [label, setLabel] = useState("");
  const [required, setRequired] = useState(false);
  const [gov, setGov] = useState(false);
  const [words, setWords] = useState("");
  const [hint, setHint] = useState("");
  const [picked, setPicked] = useState<string[]>([]);
  const [newFields, setNewFields] = useState<{ label: string; type: FieldType; required: boolean }[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    if (!label.trim()) { setError("请填写文件名称"); return; }
    if (schema.files.some((f) => f.label === label.trim())) { setError("已经有同名的文件"); return; }
    setBusy(true); setError(null);
    try {
      const key = newFileKey(schema);
      const file: ManifestFileReq = { key, label: label.trim(), group, required, submitToGov: gov, multiple: true, accept: ["pdf", "jpg", "jpeg"], ...(splitWords(words).length ? { aliases: splitWords(words) } : {}), ...(hint.trim() ? { hint: hint.trim() } : {}) };
      let next: ManifestSchema = { ...schema, files: [...schema.files, file] };
      for (const nf of newFields.filter((f) => f.label.trim())) {
        next = { ...next, fields: [...next.fields, { key: newFieldKey(next), label: nf.label.trim(), type: nf.type, required: nf.required, sourceFiles: [key] }] };
      }
      next = { ...next, version: schema.version + 1, fields: next.fields.map((f) => (picked.includes(f.key) ? { ...f, sourceFiles: [...new Set([...(f.sourceFiles ?? []), key])] } : f)) };
      await onSave(next);
      onClose();
    } catch (e) { setError(errorText(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="modal-mask" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true" aria-label="添加文件" style={{ maxWidth: 720 }}>
        <h2>在「{group}」里添加文件</h2>
        <div className="form-grid">
          <label className="field"><span className="lbl">文件名称<span className="req">*</span></span><input aria-label="文件名称" value={label} maxLength={60} onChange={(e) => setLabel(e.target.value)} placeholder="例如：担保人身份证" /></label>
          <label className="field"><span className="lbl">文件名关键词（上传目录时自动归到这里，逗号分隔）</span><input aria-label="文件名关键词" value={words} onChange={(e) => setWords(e.target.value)} placeholder="例如：sponsor, 担保人" /></label>
        </div>
        <label className="field"><span className="lbl">说明（可选）</span><input aria-label="文件说明" value={hint} maxLength={300} onChange={(e) => setHint(e.target.value)} placeholder="例如：ICA 上传类别 Others" /></label>
        <div className="row">
          <label className="check"><input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} /><span>必需（没传不能开始）</span></label>
          <label className="check"><input type="checkbox" checked={gov} onChange={(e) => setGov(e.target.checked)} /><span>要上传给政府</span></label>
        </div>
        <div className="lbl" style={{ marginTop: 10 }}>这份文件提供哪些字段</div>
        <div className="fb-pick">
          {schema.fields.map((f) => (
            <label key={f.key} className="check">
              <input type="checkbox" checked={picked.includes(f.key)} onChange={(e) => setPicked((p) => (e.target.checked ? [...p, f.key] : p.filter((k) => k !== f.key)))} />
              <span>{f.label}{f.required ? " *" : ""}</span>
            </label>
          ))}
        </div>
        <div className="lbl" style={{ marginTop: 10 }}>新建字段（这份文件提供、字段表里还没有的）</div>
        {newFields.map((nf, i) => (
          <div className="row" key={i} style={{ gap: 6, marginBottom: 6 }}>
            <input aria-label="新字段名称" placeholder="字段名称" value={nf.label} onChange={(e) => setNewFields((l) => l.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} style={{ flex: 1 }} />
            <select aria-label="新字段类型" value={nf.type} onChange={(e) => setNewFields((l) => l.map((x, j) => (j === i ? { ...x, type: e.target.value as FieldType } : x)))} style={{ width: "auto" }}>
              {TYPES.filter(([t]) => t !== "enum").map(([t, l]) => <option key={t} value={t}>{l}</option>)}
            </select>
            <label className="check"><input type="checkbox" checked={nf.required} onChange={(e) => setNewFields((l) => l.map((x, j) => (j === i ? { ...x, required: e.target.checked } : x)))} /><span>必填</span></label>
            <button className="btn btn-sm" onClick={() => setNewFields((l) => l.filter((_, j) => j !== i))}>删</button>
          </div>
        ))}
        <button className="btn btn-sm" onClick={() => setNewFields((l) => [...l, { label: "", type: "string", required: true }])}>＋ 新字段</button>
        {error && <p className="notice err">{error}</p>}
        <div className="actions">
          <button className="btn" disabled={busy} onClick={onClose}>取消</button>
          <button className="btn btn-primary" disabled={busy} onClick={() => void save()}>{busy ? "保存中…" : "添加"}</button>
        </div>
      </div>
    </div>
  );
}

/** 新增一个字段（可以不关联文件：每次手填 / 写在补充说明里） */
export function AddFieldDialog({ schema, onSave, onClose }: { schema: ManifestSchema; onSave: (s: ManifestSchema) => Promise<void>; onClose: () => void }) {
  const [label, setLabel] = useState("");
  const [type, setType] = useState<FieldType>("string");
  const [options, setOptions] = useState("");
  const [required, setRequired] = useState(true);
  const [hint, setHint] = useState("");
  const [files, setFiles] = useState<string[]>([]);
  // 直接输入（手机号 / 邮箱这类材料里没有的）：不关联文件、不自动识别
  const [inputOnly, setInputOnly] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    if (!label.trim()) { setError("请填写字段名称"); return; }
    if (schema.fields.some((f) => f.label === label.trim())) { setError("已经有同名的字段"); return; }
    if (type === "enum" && splitWords(options).length < 2) { setError("下拉选项至少两个，用逗号分隔"); return; }
    setBusy(true); setError(null);
    try {
      const f: ManifestField = { key: newFieldKey(schema), label: label.trim(), type, required, ...(type === "enum" ? { options: splitWords(options) } : {}), ...(hint.trim() ? { hint: hint.trim() } : {}), ...(inputOnly ? { inputOnly: true } : files.length ? { sourceFiles: files } : {}) };
      await onSave({ ...schema, version: schema.version + 1, fields: [...schema.fields, f] });
      onClose();
    } catch (e) { setError(errorText(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="modal-mask" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true" aria-label="新增字段" style={{ maxWidth: 640 }}>
        <h2>新增字段</h2>
        <div className="form-grid">
          <label className="field"><span className="lbl">字段名称<span className="req">*</span></span><input aria-label="字段名称" value={label} maxLength={60} onChange={(e) => setLabel(e.target.value)} placeholder="例如：担保人与申请人认识多久" /></label>
          <label className="field"><span className="lbl">类型</span>
            <select aria-label="字段类型" value={type} onChange={(e) => setType(e.target.value as FieldType)}>{TYPES.map(([t, l]) => <option key={t} value={t}>{l}</option>)}</select>
          </label>
        </div>
        {type === "enum" && <label className="field"><span className="lbl">下拉选项（逗号分隔，照政府页面原文）</span><input aria-label="下拉选项" value={options} onChange={(e) => setOptions(e.target.value)} /></label>}
        <label className="field"><span className="lbl">说明 / 识别提示（可选）</span><input aria-label="字段说明" value={hint} maxLength={300} onChange={(e) => setHint(e.target.value)} /></label>
        <label className="check"><input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} /><span>必填（取不到时提醒、挡住开始）</span></label>
        <div className="lbl" style={{ marginTop: 10 }}>值从哪里来</div>
        <label className="check"><input type="radio" name="field-source" checked={inputOnly} onChange={() => setInputOnly(true)} /><span>直接输入（手机号、邮箱这类材料里没有的，不自动识别）</span></label>
        <label className="check"><input type="radio" name="field-source" checked={!inputOnly} onChange={() => setInputOnly(false)} /><span>从文件识别</span></label>
        {!inputOnly && (
          <>
            <div className="small muted" style={{ marginTop: 6 }}>由哪些文件提供（可以都不勾：从全部材料和补充说明里找）</div>
            <div className="fb-pick">
              {schema.files.map((c) => (
                <label key={c.key} className="check">
                  <input type="checkbox" checked={files.includes(c.key)} onChange={(e) => setFiles((p) => (e.target.checked ? [...p, c.key] : p.filter((k) => k !== c.key)))} />
                  <span>{c.group ? `${c.group} / ` : ""}{c.label}</span>
                </label>
              ))}
            </div>
          </>
        )}
        {error && <p className="notice err">{error}</p>}
        <div className="actions">
          <button className="btn" disabled={busy} onClick={onClose}>取消</button>
          <button className="btn btn-primary" disabled={busy} onClick={() => void save()}>{busy ? "保存中…" : "新增"}</button>
        </div>
      </div>
    </div>
  );
}

/** 改文件类别：名称、大类、文件名关键词；或删掉这个类别（它的文件回到「未归类」，字段不再关联它） */
export function EditFileDialog({ schema, cat, onSave, onRemove, onClose }: {
  schema: ManifestSchema; cat: ManifestFileReq; onSave: (s: ManifestSchema) => Promise<void>; onRemove: () => Promise<void>; onClose: () => void;
}) {
  const [label, setLabel] = useState(cat.label);
  const [group, setGroup] = useState(cat.group ?? "");
  const [words, setWords] = useState((cat.aliases ?? []).join(", "));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setError(null);
    try { await fn(); onClose(); } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  };
  const save = () => {
    if (!label.trim()) { setError("请填写文件名称"); return; }
    if (schema.files.some((f) => f.key !== cat.key && f.label === label.trim())) { setError("已经有同名的文件"); return; }
    const aliases = splitWords(words);
    void run(() => onSave({ ...schema, version: schema.version + 1, files: schema.files.map((f) => (f.key === cat.key ? { ...f, label: label.trim(), group: group.trim() || undefined, aliases: aliases.length ? aliases : undefined } : f)) }));
  };
  const groups = [...new Set(schema.files.map((f) => f.group).filter((g): g is string => !!g))];
  return (
    <div className="modal-mask" onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="modal" role="dialog" aria-modal="true" aria-label="修改文件类别" style={{ maxWidth: 560 }}>
        <h2>修改文件类别</h2>
        <label className="field"><span className="lbl">文件名称<span className="req">*</span></span><input aria-label="文件名称" value={label} maxLength={60} onChange={(e) => setLabel(e.target.value)} /></label>
        <label className="field"><span className="lbl">大类</span>
          <input aria-label="大类" list="fb-groups" value={group} maxLength={30} onChange={(e) => setGroup(e.target.value)} placeholder="例如：身份信息" />
          <datalist id="fb-groups">{groups.map((g) => <option key={g} value={g} />)}</datalist>
        </label>
        <label className="field"><span className="lbl">文件名关键词（上传目录时自动归到这里，逗号分隔）</span><input aria-label="文件名关键词" value={words} onChange={(e) => setWords(e.target.value)} /></label>
        {error && <p className="notice err">{error}</p>}
        <div className="actions">
          <button className="btn btn-danger" disabled={busy} onClick={() => { if (confirm(`删除文件类别「${cat.label}」？这类的文件会回到「未归类」。`)) void run(onRemove); }}>删除这个类别</button>
          <span className="spacer" />
          <button className="btn" disabled={busy} onClick={onClose}>取消</button>
          <button className="btn btn-primary" disabled={busy} onClick={save}>{busy ? "保存中…" : "保存"}</button>
        </div>
      </div>
    </div>
  );
}
